import { Classes, Switch } from "@blueprintjs/core";
import { ButtonContainer, IDataTableColumn, showError, showSuccess, ToolTipButton } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";
import {
  ApprenantApi,
  DashboardApi,
  DashboardApprenantNonInscritsDto,
  DashboardApprenantsNonInscritsSearch,
  EIfapmeSide,
} from "../../../api";
import { ERoutes } from "../../../AppRouter";
import { LinkButton, SearchTablePage } from "../../../components";
import { useDialog, useEventsContext, useGlobalData } from "../../../contexts";
import { useAbortableApiServiceFactory, useApiService, useManageError, useTheme, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";

const StyledSwitch = styled(Switch)<{ color: string }>`
  & .${Classes.SWITCH} {
    &:hover {
      input:checked ~ .${Classes.CONTROL_INDICATOR} {
        background: ${props => props.color};
      }
    }
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export interface IDashboardApprenantsNonInscritsProps {
}

export const DashboardApprenantsNonInscrits: React.FunctionComponent<IDashboardApprenantsNonInscritsProps> = props => {
  const { t } = useTl();
  const { ifapmeSide, theme } = useTheme();
  const { currentAnneeScolaire } = useGlobalData();
  const api = useApiService(DashboardApi);
  const apprenantApi = useApiService(ApprenantApi);
  const { manageError } = useManageError();

  const [displayHidden, setDisplayHidden] = React.useState(false);

  const apiFactory = useAbortableApiServiceFactory(DashboardApi);
  const lastAbortController = React.useRef<AbortController>();
  const searchFunction = React.useCallback(
    (sObj?: DashboardApprenantsNonInscritsSearch) => {
      sObj.ifapmeSide = ifapmeSide === "walter" ? EIfapmeSide.Walter : EIfapmeSide.Hope;
      sObj.hideMasquees = !displayHidden;
      const { api: abortableApi, abortController } = apiFactory();
      lastAbortController.current = abortController;
      return abortableApi.dashboardSearchApprenantsNonInscrits({ DashboardApprenantsNonInscritsSearch: sObj });
    },
    [apiFactory, displayHidden, ifapmeSide]
  );

  const { dispatchEvent } = useEventsContext();
  const [rowLoading, setRowLoading] = React.useState(null);

  const { showDialogPromise } = useDialog();
  const masquerDemande = React.useCallback(
    async (idapprenant: number) => {
      const res = await showDialogPromise({
        title: t(ETLCodes.MasquerLigneConfirmTitle),
        message: t(ETLCodes.MasquerLigneConfirmMessage)
      });
      if (res === "yes") {
        setRowLoading(idapprenant);
        await api.dashboardMasquerApprenantNonInscrit({
          idapprenant: idapprenant,
          anneeScolaire: currentAnneeScolaire?.idanneeScolaire
        });
        setRowLoading(null);
        dispatchEvent("SEARCH_TABLE_REFRESH");
        dispatchEvent("DASHBOARD_COUNTS_REFRESH_Inscriptions");
      }
    },
    [api, currentAnneeScolaire?.idanneeScolaire, dispatchEvent, showDialogPromise, t]
  );

  const sendEmail = React.useCallback(async (row: DashboardApprenantNonInscritsDto) => {
    setRowLoading(row.idapprenant);
    const res = await apprenantApi.apprenantGetEmails({ request_body: [row.idapprenant] });
    setRowLoading(null);
    if (!res) {
      showError(t(ETLCodes.ApprenantHasNoEmail));
      return;
    }

    const confirmed = await showDialogPromise({
      title: t(ETLCodes.ApprenantNonInscrtitsSendEmainConfirmTitle),
      message: t(ETLCodes.ApprenantNonInscrtitsSendEmainConfirmMessage)
    });
    if (confirmed === "yes") {
      const el = document.createElement("a");
      el.setAttribute("href", `mailto:${res.azureEmails}?bcc=${res.emails}`);
      el.click();

      try {
        setRowLoading(row.idapprenant);
        await api.dashboardCreateApprenantsNonInscritsSuivi({
          DashboardApprenantNonInscritsDto: row,
          ifapmeSide: ifapmeSide === "walter" ? EIfapmeSide.Walter : EIfapmeSide.Hope
        });
        showSuccess(t(ETLCodes.SuiviSuccessfullyCreated));
      } catch (e) {
        manageError(e);
      } finally {
        setRowLoading(null);
      }
    }
  }, [api, apprenantApi, ifapmeSide, manageError, showDialogPromise, t]);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: DashboardApprenantNonInscritsDto) => (
          <ButtonContainer>
            {!row.masquee &&
              <ToolTipButton
                tooltipPosition="bottom"
                tooltip={t(ETLCodes.MasquerDemande)}
                minimal
                icon="cross"
                onClick={() => masquerDemande(row.idapprenant)}
                loading={rowLoading === row.idapprenant}
                disabled={!!rowLoading && rowLoading !== row.idapprenant}
              />}
            <ToolTipButton
              tooltipPosition="bottom"
              tooltip={t(ETLCodes.VoirInscriptionsApprenant)}
              minimal
              icon="eye-open"
              onClick={() =>
                window.open(`#${ERoutes.apprenant}/${row.idapprenant}/inscription/edit`)
              }
            />
            <ToolTipButton
              tooltipPosition="bottom"
              tooltip={t(ETLCodes.SendAnEmail)}
              minimal
              icon="envelope"
              onClick={() => sendEmail(row)}
              loading={rowLoading === row.idapprenant}
              disabled={!!rowLoading && rowLoading !== row.idapprenant}
            />
          </ButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.Concerne),
        fieldName: "apprenant",
        render: (row: DashboardApprenantNonInscritsDto) => (
          <LinkButton
            text={row.apprenant}
            onClick={() => window.open(`#${ERoutes.apprenant}/${row.idapprenant}/signaletique/view`)}
          />
        )
      },
      {
        header: () => t(ETLCodes.DateDebutConvention),
        fieldName: "dateDebut"
      },
      {
        header: () => t(ETLCodes.CodeContrat),
        fieldName: "codeContrat"
      }
    ],
    [masquerDemande, rowLoading, sendEmail, t]
  );

  const onAbort = React.useCallback(() => lastAbortController.current?.abort(), []);

  const getCriteria = React.useCallback(async () => {
    return await api.dashboardGetApprenantsNonInscritsCriterias();
  }, [api]);

  const rightElement = React.useMemo(
    () => (
      <ButtonsContainer>
        <StyledSwitch
          color={theme.primaryColor}
          label={t(ETLCodes.AfficherMasquees)}
          checked={displayHidden}
          onChange={e => {
            setDisplayHidden(e.currentTarget.checked);
          }}
        ></StyledSwitch>
      </ButtonsContainer>
    ),
    [displayHidden, t, theme.primaryColor]
  );

  return (
    <SearchTablePage
      title={ETLCodes.ApprenantsNonInscrits}
      searchStateInitialSearch={false}
      withCard={false}
      columns={columns}
      getCriteriasFunction={getCriteria}
      searchFunction={searchFunction}
      onAbort={onAbort}
      rightElement={
        rightElement
      }
    />
  );
};
